/**
 * Vista de Cuentas de FinVoz (FR-ACCT-1..8, spec/01-requirements.md §3).
 *
 * Muestra:
 *  - Jerarquia de cuentas por tipo contable (asset, liability, equity, income, expense)
 *  - Saldos agregados para cuentas padre y directos para hojas (FR-ACCT-3, FR-ACCT-4)
 *  - Modal para crear cuentas (FR-ACCT-1, FR-ACCT-2)
 *  - Accion de archivado (lapida, FR-ACCT-5: nunca borrado directo)
 *  - Asistente para saldo de apertura contra Patrimonio Inicial (FR-ACCT-8)
 */

import type { AppState } from "../types.js";
import { formatMinorUnits } from "../format.js";
import { escapeHtml } from "../sanitize.js";
import { accountBalance, aggregatedBalance } from "../../core/ledger.js";
import type { Account } from "../../core/account.js";

const TYPE_NAMES: Record<Account["type"], string> = {
  asset: "Activos (Bancos, Efectivo, Inversiones)",
  liability: "Pasivos (Tarjetas, Préstamos)",
  equity: "Patrimonio Neto (Capital, Fondos Propios)",
  income: "Ingresos (Nómina, Ventas, Rentas)",
  expense: "Gastos (Vivienda, Comida, Suministros)",
};

export function renderAccounts(state: AppState): string {
  const currency = state.currency || "PEN";
  const scale = 2;
  const rawEntries = state.entries.map((e) => e.entry);

  const types: Account["type"][] = ["asset", "liability", "equity", "income", "expense"];

  return `
    <div class="accounts-view" data-testid="accounts-view">
      <div class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Plan de Cuentas</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Estructura contable jerárquica con saldos derivados exclusivamente del libro (INV-4)
            </div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary" data-testid="opening-balance-btn" data-action="open-opening-balance-modal">
              ⚖️ Saldo de Apertura
            </button>
            <button class="btn-primary" data-testid="create-account-btn" data-action="open-new-account-modal">
              ➕ Nueva Cuenta
            </button>
          </div>
        </div>

        ${types.map((type) => {
          const typeAccounts = state.accounts.filter((a) => a.type === type && !a.archived);
          const archivedCount = state.accounts.filter((a) => a.type === type && a.archived).length;

          return `
            <div style="margin-bottom: 24px;">
              <h3 style="font-size: 0.95rem; font-weight: 600; color: var(--accent); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;">
                ${TYPE_NAMES[type]} (${typeAccounts.length})
              </h3>
              ${typeAccounts.length === 0
                ? `<div style="font-size: 0.85rem; color: var(--text-muted); padding: 8px 0;">No hay cuentas de este tipo.</div>`
                : `
                  <div class="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Nombre de Cuenta</th>
                          <th>ID</th>
                          <th>Moneda</th>
                          <th style="text-align: right;">Saldo Derivado</th>
                          <th style="text-align: right;">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${typeAccounts.map((acc) => {
                          const isParent = state.accounts.some((a) => a.parentId === acc.accountId);
                          const bal = isParent
                            ? aggregatedBalance(acc.accountId, state.accounts, rawEntries, currency, scale)
                            : accountBalance(acc.accountId, rawEntries, currency, scale);

                          return `
                            <tr data-testid="account-row-${escapeHtml(acc.accountId)}">
                              <td style="font-weight: 500;">
                                ${acc.parentId ? `<span style="color: var(--text-muted); margin-right: 8px;">└─</span>` : ""}
                                ${escapeHtml(acc.name)}
                                ${isParent ? `<span class="badge" style="background-color: var(--bg-tertiary); color: var(--text-secondary); margin-left: 6px;">Padre</span>` : ""}
                                ${acc.instrumentId ? `<span class="badge" style="background-color: rgba(56, 189, 248, 0.2); color: var(--accent); margin-left: 6px;">${escapeHtml(acc.instrumentId)}</span>` : ""}
                              </td>
                              <td style="font-family: var(--font-mono); font-size: 0.8rem; color: var(--text-muted);">${escapeHtml(acc.accountId)}</td>
                              <td>${escapeHtml(acc.currency)}</td>
                              <td style="text-align: right; font-family: var(--font-mono); font-weight: 600;">
                                ${formatMinorUnits(bal.amountMinor, currency, scale)}
                              </td>
                              <td style="text-align: right;">
                                <button class="btn-secondary" style="font-size: 0.75rem; padding: 2px 8px; color: var(--warning);" data-testid="archive-account-${escapeHtml(acc.accountId)}" title="Archivar cuenta (lápida, FR-ACCT-5)" data-action="archive-account" data-account-id="${escapeHtml(acc.accountId)}">
                                  Archivar
                                </button>
                              </td>
                            </tr>
                          `;
                        }).join("")}
                      </tbody>
                    </table>
                  </div>
                `}
              ${archivedCount > 0 ? `<div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">(${archivedCount} cuenta(s) archivadas en el histórico)</div>` : ""}
            </div>
          `;
        }).join("")}
      </div>

      <!-- Modal Nueva Cuenta (FR-ACCT-1) -->
      <div id="new-account-modal" class="modal-overlay" style="display: none;">
        <div class="modal-content">
          <div class="card-header">
            <h3 class="card-title">Crear Nueva Cuenta</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-new-account-modal">&times;</button>
          </div>
          <form id="new-account-form" data-form="new-account">
            <div style="margin-bottom: 12px;">
              <label for="new-acc-name" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Nombre:</label>
              <input type="text" id="new-acc-name" data-testid="new-acc-name" required placeholder="Ej: Cuenta de Ahorro" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="new-acc-type" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Tipo Contable:</label>
              <select id="new-acc-type" data-testid="new-acc-type" required style="width: 100%;">
                <option value="asset">Activo (Asset)</option>
                <option value="liability">Pasivo (Liability)</option>
                <option value="equity">Patrimonio (Equity)</option>
                <option value="income">Ingreso (Income)</option>
                <option value="expense">Gasto (Expense)</option>
              </select>
            </div>
            <div style="margin-bottom: 12px;">
              <label for="new-acc-parent" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Padre (Opcional):</label>
              <select id="new-acc-parent" data-testid="new-acc-parent" style="width: 100%;">
                <option value="">Ninguna (Nivel raíz)</option>
                ${state.accounts.filter((a) => !a.archived).map((a) => `<option value="${escapeHtml(a.accountId)}">${escapeHtml(a.name)} (${escapeHtml(a.type)})</option>`).join("")}
              </select>
            </div>
            <div style="margin-bottom: 16px;">
              <label for="new-acc-instrument" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Instrumento de Inversión (Opcional):</label>
              <input type="text" id="new-acc-instrument" data-testid="new-acc-instrument" placeholder="Ej: VWCE.DE, AAPL, BTC-EUR" style="width: 100%;" />
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-new-account-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="submit-new-acc-btn">Guardar Cuenta</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal Saldo de Apertura (FR-ACCT-8) -->
      <div id="opening-balance-modal" class="modal-overlay" style="display: none;">
        <div class="modal-content">
          <div class="card-header">
            <h3 class="card-title">Registrar Saldo de Apertura</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-opening-balance-modal">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 16px;">
            El saldo inicial no es un campo especial: se crea un asiento equilibrado normal contra la cuenta de Patrimonio Inicial (FR-ACCT-8).
          </p>
          <form id="opening-balance-form" data-form="opening-balance">
            <div style="margin-bottom: 12px;">
              <label for="ob-account" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta a Dotar:</label>
              <select id="ob-account" data-testid="ob-account" required style="width: 100%;">
                ${state.accounts.filter((a) => a.type === "asset" && !a.archived).map((a) => `<option value="${escapeHtml(a.accountId)}">${escapeHtml(a.name)}</option>`).join("")}
              </select>
            </div>
            <div style="margin-bottom: 12px;">
              <label for="ob-amount" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe Inicial (${currency === "PEN" ? "S/" : currency === "USD" ? "$" : "€"}):</label>
              <input type="number" step="0.01" min="0.01" id="ob-amount" data-testid="ob-amount" required placeholder="0.00" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 16px;">
              <label for="ob-date" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Fecha de Apertura:</label>
              <input type="date" id="ob-date" data-testid="ob-date" required style="width: 100%;" value="${escapeHtml(new Date().toISOString().slice(0, 10))}" />
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-opening-balance-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="submit-ob-btn">Crear Asiento de Apertura</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `;
}
